import React, { useState, useEffect } from "react";
import "./TradingIndicatorsDashboard.css";
import PositionDetails from "./PositionDetails"; // Import modal component
import { buildScopedApiUrl, ensureArray, getApiHeaders } from "./config";
import { useWorkspace } from "./workspace/WorkspaceContext";

function TradingIndicatorsDashboard() {
  const { workspace } = useWorkspace();
  const [tradingData, setTradingData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [timeframes, setTimeframes] = useState([]);
  const [candleCloseTimes, setCandleCloseTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ Reset filters to "ALL" on refresh
  const [selectedActiveStatus, setSelectedActiveStatus] = useState("ALL");
  const [selectedTimeframe, setSelectedTimeframe] = useState("");
  const [selectedCandleCloseTime, setSelectedCandleCloseTime] = useState("");

  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); // ✅ New State for Search


  useEffect(() => {
    fetchData();
    resetFilters(); // ✅ Ensuring filters reset correctly in UI

    const interval = setInterval(() => {    
      fetchData();
      resetFilters();
    }, 60000); // Refresh every 20 seconds for testing

    return () => clearInterval(interval);
  }, [workspace]);

  const resetFilters = () => {
    setSelectedActiveStatus("ALL");
    setSelectedTimeframe("");
    setSelectedCandleCloseTime("");
  };

  const fetchData = async () => {
    try {
      const response = await fetch(
        buildScopedApiUrl('/api/bot/ui/get/getLatestSignalFlyTradingSignalsForAll', workspace),
        {
          headers: getApiHeaders()
        }
      );
      if (!response.ok) throw new Error("Failed to fetch data");

      const text = await response.text();
      if (text) {
        const rawData = JSON.parse(text);
        const safeData = ensureArray(rawData);
        if (!Array.isArray(rawData)) {
          console.warn("Unexpected trading indicators payload shape", workspace, rawData);
        }
        setTradingData(safeData);
        extractUniqueFilters(safeData);
        applyFilters(safeData, "ALL", "", "");
        setError(null);
      } else {
        setTradingData([]);
        setFilteredData([]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const extractUniqueFilters = (data) => {
    const safeData = ensureArray(data);
    const uniqueTimeframes = [...new Set(safeData.map((item) => item.timeFrame))].sort((a, b) => a - b);
    const uniqueCandleCloseTimes = [...new Set(safeData.map((item) => item.candleCloseTimeInSGT))].sort();

    setTimeframes(uniqueTimeframes);
    setCandleCloseTimes(uniqueCandleCloseTimes);
  };

  useEffect(() => {
    applyFilters(tradingData, selectedActiveStatus, selectedTimeframe, selectedCandleCloseTime);
}, [selectedActiveStatus, selectedTimeframe, selectedCandleCloseTime, searchTerm]); // ✅ Added searchTerm


  const applyFilters = (data, activeStatus, timeframe, candleCloseTime) => {
    const safeData = ensureArray(data);
    if (safeData.length === 0) {
      setFilteredData([]);
      return;
    }

    let filtered = [...safeData];

    if (activeStatus !== "ALL") {
      filtered = filtered.filter((item) => item.activeNonActive === activeStatus);
    }

    if (timeframe) {
      filtered = filtered.filter((item) => item.timeFrame === parseInt(timeframe));
    }

    if (candleCloseTime) {
      filtered = filtered.filter((item) => item.candleCloseTimeInSGT === candleCloseTime);
    }

    // ✅ Fix: Remove duplicates, sort DCA Symbols
    filtered.forEach((signal) => {
      const uniqueDCABullish = [...new Set((signal.dcaSymbols || []).filter((sym) => sym.includes("LONG")))].sort();
      const uniqueDCABearish = [...new Set((signal.dcaSymbols || []).filter((sym) => sym.includes("SHORT")))].sort();
      signal.dcaBullish = uniqueDCABullish;
      signal.dcaBearish = uniqueDCABearish;
    });

        // ✅ Apply Search Filtering
        if (searchTerm) {
          const regex = new RegExp(searchTerm, "i"); // Case-insensitive pattern match
          filtered = filtered.map((signal) => {
              const filteredCategories = {};
              Object.entries(signal).forEach(([key, symbols]) => {
                  if (Array.isArray(symbols)) {
                      filteredCategories[key] = symbols.filter((symbol) => regex.test(symbol));
                  }
              });
              return { ...signal, ...filteredCategories };
          });
      }

    setFilteredData(filtered);
  };

  const openTradingViewChart = (symbol) => {
    const cleanedSymbol = symbol.replace(/\s*(LONG|SHORT)$/, "");
    const suffix = workspace.marketType === 'PERPS' ? '.P' : '';
    const tradingViewUrl = `https://www.tradingview.com/chart/?symbol=${workspace.exchange}:${cleanedSymbol}${suffix}`;
    window.open(tradingViewUrl, "_blank");
  };

  const openPositionModal = (symbol) => {
    setSelectedSymbol(symbol.replace(/\s*(LONG|SHORT)$/, "") + "_UMCBL"); // ✅ Fixed symbol formatting
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedSymbol(null);
  };

  const CATEGORIES = {
    Bullish: [
      { key: "emaBullishCrossSymbols", label: "EMA Bullish Crossover" },
      { key: "dcaBullish", label: "DCA Bullish Signals" },
      { key: "stochRsiBullishCrossSymbols", label: "Stoch RSI Bullish Crossover" },
      { key: "rsiBullishReversalSymbols", label: "RSI Bullish Reversal" },
      { key: "bothStochRsiAndRsiBullishSymbols", label: "Both Stoch RSI & RSI Bullish" },
      { key: "rsiOversoldSymbols", label: "RSI Oversold" },
      { key: "stochRsiOverSoldSymbols", label: "Stoch RSI Oversold" }
    ],
    Bearish: [
      { key: "emaBearishCrossSymbols", label: "EMA Bearish Crossover" },
      { key: "dcaBearish", label: "DCA Bearish Signals" },
      { key: "stochRsiBearishCrossSymbols", label: "Stoch RSI Bearish Crossover" },
      { key: "rsiBearishReversalSymbols", label: "RSI Bearish Reversal" },
      { key: "bothStochRsiAndRsiBearishSymbols", label: "Both Stoch RSI & RSI Bearish" },
      { key: "rsiOverboughtSymbols", label: "RSI Overbought" },
      { key: "stochRsiOverBoughtSymbols", label: "Stoch RSI Overbought" }
    ]
  };

  return (
    <div className="trading-dashboard">
      <h2 className="dashboard-title">📊 Trading Signals Dashboard</h2>

      {/* Filters Section */}
      <div className="filters">
        <select onChange={(e) => {
          setSelectedActiveStatus(e.target.value);
          applyFilters(tradingData, e.target.value, selectedTimeframe, selectedCandleCloseTime);
        }} 
        value={selectedActiveStatus} className="dropdown">
          <option value="ALL">All</option>
          <option value="ACTIVE">Active</option>
          <option value="NON-ACTIVE">Non-Active</option>
        </select>

        <select onChange={(e) => setSelectedTimeframe(e.target.value)} value={selectedTimeframe} className="dropdown">
          <option value="">🕒 All Timeframes</option>
          {timeframes.map((tf) => (
            <option key={tf} value={tf}>{tf} min</option>
          ))}
        </select>

        <select onChange={(e) => setSelectedCandleCloseTime(e.target.value)} value={selectedCandleCloseTime} className="dropdown">
          <option value="">📅 All Candle Close Times</option>
          {candleCloseTimes.map((ct) => (
            <option key={ct} value={ct}>{ct}</option>
          ))}
        </select>
        <div className="search-container">
          <input
            type="text"
            placeholder="🔍 Search by symbol..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>


      </div>

      <div className="categories">
        {Object.entries(CATEGORIES).map(([category, subcategories]) => (
          <div key={category} className={`category ${category.toLowerCase()}`}>
            <h3 className={`category-title ${category.toLowerCase()}-title`}>
              {category === "Bullish" ? "📈 Bullish Signals" : "📉 Bearish Signals"}
            </h3>
            {subcategories.map(({ key, label }) => (
              <div key={key} className="category-section">
                <h4 className="subcategory-title">{label}</h4>
                <ul className="symbol-list">
                  {[...new Set(ensureArray(filteredData).flatMap((signal) => ensureArray(signal?.[key])))].sort().map((symbol, idx) => (
                    <li key={idx} className="symbol-item">
                      {symbol.replace(/\s*(LONG|SHORT)$/, "")}
                      <span className="chart-icon" onClick={() => openTradingViewChart(symbol)}>📈</span>
                      {selectedActiveStatus === "ACTIVE" && (
                                             <span className="position-icon" onClick={() => openPositionModal(symbol)} title="View Position Details" style={{ marginLeft: "10px", cursor: "pointer" }}>
                                               🚀
                                             </span>
                                           )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ))}
         {showModal && selectedSymbol && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="close-modal" onClick={closeModal}>❌</button>
            <PositionDetails symbol={selectedSymbol} />
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default TradingIndicatorsDashboard;
